import { useState } from 'react';

import { TParsedProductInstance } from '@lib/core/products/types';
import { IHandleUpdateProductRatingProps } from '@lib/core/users/hooks/useProductRate';
import { IProductRatingOptions, TProductRateValue } from '@lib/core/users/slices/productRate';
import MixpanelTracker from '@lib/tools/dat/mixpanel';
import { TGameRateAction } from '@lib/tools/gamification/types';
import { localeCommon } from '@lib/tools/locale/source/web/common';

import Button from '@components/web/src/atoms/Buttons/Button';
import { CloseButton } from '@components/web/src/atoms/Buttons/CloseButton/CloseButton';
import { ProductRateListItem } from '@components/web/src/atoms/ProductRating/ProductRatingListItem/ProductRateListItem';
import { Flexbox } from '@components/web/src/foundations/Flexbox/Flexbox';
import { Text } from '@components/web/src/foundations/Text/Text';
import { COLORS } from '@components/web/src/styles';
import { ModalWrapper } from '@components/web/src/templates/Modals/ModalWrapper/ModalWrapper';
import * as S from '@components/web/src/templates/Modals/ProductRateModal/styles';

interface Props {
  isModalOpen: boolean;
  isLoading: boolean;
  parsedProductInstanceData: TParsedProductInstance;
  productRateOptions: IProductRatingOptions[];
  hideModal: () => void;
  handleOpenDiscoveryQuizModal: () => void;
  handleUpdateProductRating: ({ productRate, slug, productId, productName }: IHandleUpdateProductRatingProps) => void;
  handleRateGameProduct?: (parsedProductInstanceData: TParsedProductInstance, rateAction: TGameRateAction) => void;
}

type TProductRateItemData = {
  rateValue: null | TProductRateValue;
  slug: string;
};

export const ProductRateModal = ({
  isModalOpen,
  isLoading,
  parsedProductInstanceData,
  productRateOptions,
  hideModal,
  handleOpenDiscoveryQuizModal,
  handleUpdateProductRating,
  handleRateGameProduct,
}: Props) => {
  const [productRateItemData, setProductRateItemData] = useState<TProductRateItemData>({
    rateValue: null,
    slug: '',
  });

  const handleClick = (productRateData: IProductRatingOptions) => {
    setProductRateItemData({ rateValue: productRateData?.rateValue, slug: productRateData?.slug });
  };
  const { productRate, handleSetDiscoveryQuizProducts, productName, productProducerName, productId } =
    parsedProductInstanceData;
  const { titleText, buttonText } = localeCommon.ratingModal;

  return (
    <ModalWrapper isAnimated isOpen={isModalOpen} onClose={hideModal}>
      <S.ProductRateModalContainer>
        <CloseButton handleClick={hideModal} position="right" />
        <S.ContentWrapper direction="column" gap={16}>
          <S.HeadingWrapper direction="column" gap={0}>
            {productName && (
              <Text color={COLORS.base.black} fontType="grace" size="subtitle1" text={productName} weight="semibold" />
            )}
            {productProducerName && (
              <Text color={COLORS.base.black} size="body2" text={productProducerName} weight="semibold" />
            )}
          </S.HeadingWrapper>

          <Text color={COLORS.green[600]} size="body1" text={titleText} weight="bold" />

          <Flexbox direction="column" gap={10}>
            {productRateOptions.map((data, index) => (
              <ProductRateListItem
                key={index}
                productRate={data.rateValue}
                isActive={
                  productRateItemData.rateValue === data.rateValue ||
                  (!productRateItemData.rateValue && data.rateValue === productRate?.rateValue)
                }
                onHandleClick={() => handleClick(data)}
              />
            ))}
          </Flexbox>
          <S.ButtonWrapper>
            <Button
              isFullWidth
              disabled={!productRateItemData?.rateValue || productRateItemData?.rateValue === productRate?.rateValue}
              fontSize="body1"
              isLoading={isLoading}
              size="sm"
              text={buttonText}
              textWeight="medium"
              handleClick={async () => {
                await handleUpdateProductRating({
                  productId,
                  productName,
                  productRate: productRateItemData?.rateValue,
                  slug: productRateItemData?.slug,
                });

                if (handleRateGameProduct) {
                  handleRateGameProduct(parsedProductInstanceData, 'rate');
                }

                if (!productRate && handleOpenDiscoveryQuizModal) {
                  handleSetDiscoveryQuizProducts();
                  handleOpenDiscoveryQuizModal();
                }

                MixpanelTracker.events.productFeedback(parsedProductInstanceData, productRateItemData?.rateValue);

                hideModal();
              }}
            />
          </S.ButtonWrapper>
        </S.ContentWrapper>
      </S.ProductRateModalContainer>
    </ModalWrapper>
  );
};
